/**
 * Wizard field validators (PURE) — title/slug, concept length, and reference
 * URL rules shared by the concept-first wizard flow (./wizard/flow.ts).
 * Everything decision-shaped lives in the @djgg/creations spec registry,
 * never here.
 */
import { slugify, isValidSlug, SLUG_MIN_LEN, SLUG_MAX_LEN } from '@djgg/creations';

export const TITLE_MIN_LEN = 3;
export const TITLE_MAX_LEN = 60;
/** The concept-step free-text bounds (the concept doubles as the first
 *  build's prompt, and analyzeConcept needs real material to work with). */
export const CONCEPT_MIN_LEN = 40;
export { CONCEPT_MAX_LEN } from '@djgg/creations';
/** Reference URLs the concept step accepts (the analyzeConcept cap). */
export const MAX_REFERENCE_URLS = 3;

/** Title errors (`title` for length problems, `slug` for un-sluggable titles);
 *  empty map = valid. */
export function validateTitle(rawTitle: string): Partial<Record<'title' | 'slug', string>> {
  const title = rawTitle.trim();
  if (title.length < TITLE_MIN_LEN) {
    return { title: `Title must be at least ${TITLE_MIN_LEN} characters.` };
  }
  if (title.length > TITLE_MAX_LEN) {
    return { title: `Title must be at most ${TITLE_MAX_LEN} characters.` };
  }
  if (!isValidSlug(slugify(title))) {
    return { slug: `Title must yield a URL slug of ${SLUG_MIN_LEN}-${SLUG_MAX_LEN} letters/numbers.` };
  }
  return {};
}

/** Basic client-side reference-URL check: parseable and https. The builder's
 *  fetcher owns the real (SSRF-guarded) validation. */
export function isValidReferenceUrl(raw: string): boolean {
  try {
    return new URL(raw.trim()).protocol === 'https:';
  } catch {
    return false;
  }
}

/** Derive the slug for a title (empty string if not derivable). */
export function wizardSlug(title: string): string {
  return slugify(title);
}
